/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, Button, message, Typography } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import { useIntl } from 'react-intl';
import { useMutation, queryCache } from 'react-query';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { parseFileName } from '~src/utils/file';
import { useProjectId } from '~src/hooks/useProjectId';
import { useTenderParamsFromUrl } from '~src/hooks/useTenderParamsFromUrl';
import { DbAttachment } from '~src/types/db';
import { updateTenderAttachment } from '~src/services/api';

export interface EditAttachmentModalProps extends Partial<ModalProps> {
  attachment?: DbAttachment; // root-level attachment (Tender documents root)
  onDone?: () => void;
}

export const EditAttachmentModal: React.FC<EditAttachmentModalProps> = ({
  attachment,
  onCancel,
  onDone,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const { tenderId } = useTenderParamsFromUrl();

  const nameInputRef = useRef<Input>(null);

  const parsedName = useMemo(() => {
    return parseFileName(attachment?.name || attachment?.title || '');
  }, [attachment]);

  const [nameVal, setNameVal] = useState('');
  const [descriptionVal, setDescriptionVal] = useState<string>(
    attachment?.description || ''
  );

  useEffect(() => {
    setNameVal(parsedName.name);
    setDescriptionVal(attachment?.description || '');
  }, [attachment, parsedName.name]);

  useEffect(() => {
    if (modalProps.visible) {
      setTimeout(() => nameInputRef.current?.focus(), 300);
    }
  }, [modalProps.visible]);

  const getFullName = () => {
    let full = nameVal.trim();
    if (parsedName.extension) full += `.${parsedName.extension}`;
    return full;
  };

  const isValid = () => {
    return Boolean(attachment && nameVal.trim());
  };

  const [doUpdate, { isLoading }] = useMutation(updateTenderAttachment, {
    onSuccess: () => {
      queryCache.invalidateQueries('tendersTenderAttachments');
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      onDone?.();
      onCancel?.({
        //
      });
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'attachment-edit-failed' }));
    },
  });

  const handleSubmit = () => {
    if (!isValid() || !attachment || !projectId || !tenderId) return;
    // @ts-ignore
    doUpdate({
      projectId: String(projectId),
      tenderId: String(tenderId),
      fileGuid: String(
        attachment?.file_id || attachment?.guid || attachment?.id
      ),
      data: {
        name: getFullName(),
        description: descriptionVal || '',
        version: null,
      },
    });
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'edit-attachment' })}
          icon={['far', 'pen']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ disabled: !isValid(), loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      footer={
        <React.Fragment>
          <Button onClick={onCancel}>
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          <Button
            type="primary"
            loading={isLoading}
            disabled={!isValid()}
            onClick={handleSubmit}>
            {intl.formatMessage({ id: 'save' })}
          </Button>
        </React.Fragment>
      }
      {...modalProps}>
      <Typography.Paragraph css={{ '&.ant-typography': { marginBottom: 8 } }}>
        {intl.formatMessage({ id: 'from' })}{' '}
        <b>{attachment?.name || attachment?.title}</b>
      </Typography.Paragraph>
      <Input
        ref={nameInputRef}
        placeholder={intl.formatMessage({ id: 'file-name' })}
        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
        suffix={parsedName.extension ? `.${parsedName.extension}` : <span />}
        value={nameVal}
        onChange={({ target: { value } }) => setNameVal(value)}
      />
      <Input.TextArea
        css={{ marginTop: 10 }}
        placeholder={intl.formatMessage({ id: 'description' })}
        value={descriptionVal}
        onChange={({ target: { value } }) => setDescriptionVal(value)}
      />
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({});
